import { FunctionComponent, MouseEvent, memo, useCallback, useContext } from 'react';
import { UserContext } from '../../../../context';
import { Story, StoryStatus } from '../../../../services';
import { useIsFollowAllowed, useStoryFollow } from '../../../hooks';
import { isStoryFollowable } from '../../../utils';
import { FollowToggle } from '../../FollowToggle';
import { NotificationToggle } from '../../NotificationToggle';
import { useStoryNotification } from './useStoryNotification';

interface StoryFollowControlsProps {
  story: Story;
}

export const StoryFollowControls: FunctionComponent<StoryFollowControlsProps> = memo(
  ({ story }) => {
    const { isAnonymous } = useContext(UserContext).userProfile;

    const isFollowAllowed = useIsFollowAllowed();

    const { isStoryFollowed, toggleStoryFollow, isFollowPending } = useStoryFollow();

    const { isStoryNotified, setStoryNotified, toggleStoryNotification, isNotificationPending } =
      useStoryNotification();

    const followHandler = useCallback(
      async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        const { id: storyId } = story;

        const isNotified = isStoryNotified(story);

        setStoryNotified({ storyId, isNotified: !isStoryFollowed(story) });

        if (await toggleStoryFollow(story)) {
          return;
        }

        setStoryNotified({ storyId, isNotified });
      },
      [isStoryFollowed, isStoryNotified, setStoryNotified, story, toggleStoryFollow]
    );

    const notificationHandler = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        toggleStoryNotification(story);
      },
      [story, toggleStoryNotification]
    );

    const { status, isReadOnly } = story;

    return (
      <>
        {isFollowAllowed && isStoryFollowable(story) && (
          <FollowToggle
            followed={isStoryFollowed(story)}
            isPending={isFollowPending}
            onClick={followHandler}
            hideBorder
          />
        )}
        {status === StoryStatus.PUBLISHED && !isReadOnly && !isAnonymous && (
          <NotificationToggle
            isEnabled={isStoryNotified(story)}
            isPending={isNotificationPending}
            onClick={notificationHandler}
          />
        )}
      </>
    );
  }
);
